import React from 'react';
import { Box, Chip, Divider, Link, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { formatBookingCurrency } from '../../utils/bookingPaymentSummary';
import { BOOKING_DRAWER_ACCENT } from '../../utils/bookingDrawerLayout';
import BookingOverpaidBanner from './BookingOverpaidBanner';
import BookingExtraChargesBreakdown from './BookingExtraChargesBreakdown';

/** Small Inclusive / Exclusive GST mode chip for payment drawers. */
export function BookingGstModeChip({ inclusive = false, isDark = false, sx = {} }) {
  const isInclusive = Boolean(inclusive);
  return (
    <Chip
      size="small"
      label={isInclusive ? 'Inclusive' : 'Exclusive'}
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        backgroundColor: isInclusive
          ? isDark
            ? 'rgba(76, 225, 182, 0.18)'
            : 'rgba(15, 118, 110, 0.12)'
          : isDark
            ? 'rgba(144, 202, 249, 0.16)'
            : 'rgba(21, 101, 192, 0.1)',
        color: isInclusive
          ? isDark
            ? '#4CE1B6'
            : '#0f766e'
          : isDark
            ? '#90caf9'
            : '#1565c0',
        '& .MuiChip-label': { px: 0.75 },
        ...sx,
      }}
    />
  );
}

/**
 * Shared payment breakdown block for booking drawers and payment collection.
 * Expects output from getPaymentSummaryData().
 */
export default function BookingPaymentBreakdown({
  summary,
  isDark = false,
  drawerTheme = {},
  showReceived = true,
  showRemaining = true,
  showReturnAmount = true,
  showTotalFirst = true,
  compact = false,
  detailLabel = 'booking total',
  bookingId,
  allowExtraChargesEdit = false,
  onAddExtraChargesClick,
}) {

  if (!summary) return null;

  const subtle = drawerTheme.subtleText || (isDark ? '#999' : '#666');
  const text = drawerTheme.text || (isDark ? '#ddd' : '#333');
  const fontSize = compact ? '0.8125rem' : '0.85rem';
  const Row = ({ label, value, valueColor, bold, labelExtra }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, pr: 1 }}>
        <Typography variant="body2" sx={{ color: subtle, fontSize }}>
          {label}
        </Typography>
        {labelExtra}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: bold ? 700 : 600,
          color: valueColor || text,
          fontSize: bold ? '0.9rem' : fontSize,
          flexShrink: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const dividerSx = {
    my: 1,
    borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
  };

  const gstLabel =
    Number(summary.gstPercentage) > 0
      ? `GST (${Number(summary.gstPercentage)}%)`
      : summary.gstIncluded
        ? 'GST (included)'
        : 'GST';

  const discountLabel =
    Number(summary.discountPercentage) > 0
      ? `Discount (${Number(summary.discountPercentage)}%)`
      : 'Discount';

  const showExtraChargesAction = Boolean(
    allowExtraChargesEdit && bookingId && typeof onAddExtraChargesClick === 'function'
  );

  return (
    <>
      {showTotalFirst ? (
        <>
          <Row
            label="Total Amount"
            value={formatBookingCurrency(summary.displayTotal ?? summary.totalAmount, summary.currency)}
            bold
          />
          <Divider sx={dividerSx} />
        </>
      ) : null}

      <Row
        label="Subtotal"
        value={formatBookingCurrency(summary.displaySubtotal ?? summary.subtotalBeforeDiscount, summary.currency)}
      />

      {summary.discountAmount > 0 ? (
        <Row
          label={discountLabel}
          value={`- ${formatBookingCurrency(summary.discountAmount, summary.currency)}`}
          valueColor={isDark ? '#ef9a9a' : '#c62828'}
        />
      ) : (
        <Row
          label={discountLabel}
          value={`- ${formatBookingCurrency(0, summary.currency)}`}
          valueColor={isDark ? '#ef9a9a' : '#c62828'}
        />
      )}

      <Row
        label={summary.afterDiscountLabel || 'After Discount'}
        value={formatBookingCurrency(
          summary.displayAfterDiscount ?? summary.subtotalAfterDiscount,
          summary.currency
        )}
      />

      {(summary.gstIncluded || summary.gstAmount > 0 || summary.displayGstAmount > 0) ? (
        <Row
          label={gstLabel}
          labelExtra={
            <BookingGstModeChip
              inclusive={Boolean(summary.inclusiveDisplay || summary.taxInclusive)}
              isDark={isDark}
            />
          }
          value={`${summary.gstPrefix || ''}${formatBookingCurrency(summary.displayGstAmount ?? summary.gstAmount, summary.currency)}`}
          valueColor={isDark ? '#90caf9' : '#1565c0'}
        />
      ) : null}

      {summary.extraChargesAmount > 0 ? (
        <BookingExtraChargesBreakdown
          items={summary.extraChargesItems}
          amount={summary.extraChargesAmount}
          baseAmount={summary.extraChargesBaseAmount}
          gstAmount={summary.extraChargesGstAmount}
          note={summary.extraChargesNote}
          currency={summary.currency}
          isDark={isDark}
          accent={BOOKING_DRAWER_ACCENT}
          compact
        />
      ) : null}

      {showExtraChargesAction ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: showReceived || showRemaining ? 0.5 : 0 }}>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={onAddExtraChargesClick}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.8125rem',
              fontWeight: 700,
              color: BOOKING_DRAWER_ACCENT,
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              p: 0,
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            Add Extra Charges
          </Link>
        </Box>
      ) : null}

      {showReceived || showRemaining ? <Divider sx={dividerSx} /> : null}

      {showReceived ? (
        <Row
          label="Received Amount"
          value={formatBookingCurrency(summary.paidAmount, summary.currency)}
          valueColor={isDark ? '#81c784' : '#2e7d32'}
        />
      ) : null}

      {showReceived && showRemaining ? <Divider sx={dividerSx} /> : null}

      {showRemaining ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
            borderRadius: '6px',
            backgroundColor:
              summary.remainingAmount > 0
                ? isDark
                  ? 'rgba(255, 152, 0, 0.1)'
                  : 'rgba(255, 152, 0, 0.05)'
                : isDark
                  ? 'rgba(76, 175, 80, 0.1)'
                  : 'rgba(76, 175, 80, 0.05)',
          }}
        >
          <Typography variant="body2" sx={{ color: subtle, fontSize, fontWeight: 600 }}>
            Remaining Balance
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.9rem',
              color:
                summary.remainingAmount > 0
                  ? isDark
                    ? '#ffb74d'
                    : '#f57c00'
                  : isDark
                    ? '#81c784'
                    : '#2e7d32',
            }}
          >
            {formatBookingCurrency(summary.remainingAmount, summary.currency)}
          </Typography>
        </Box>
      ) : null}

      {showReturnAmount && Number(summary.returnAmount) > 0 ? (
        <Box sx={{ mt: showRemaining ? 1 : 0 }}>
          <BookingOverpaidBanner
            returnAmount={summary.returnAmount}
            paidAmount={summary.paidAmount}
            totalAmount={summary.displayTotal ?? summary.totalAmount}
            currency={summary.currency}
            isDark={isDark}
            detailLabel={detailLabel}
            compact
          />
        </Box>
      ) : null}
    </>
  );
}
